import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
  TablePagination,
  Backdrop,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  deleteModifierGroup,
  createModifierGroup,
  createModifierGroupOption,
  createItem,
  createItemModifierGroup,
} from '../../../redux/actions/posMenu';
import { getTranslationValue } from '../../../constants/posMenu';
import { isPosProviderConnected } from '../../../utils/checkAccessDenied';
import { priceTransform, formatNumber } from '../../../helpers/formPrice';
import usePosMenuMap from '../hooks/usePosMenuMap';
import MenuLoadError from '../../../components/PosMenu/MenuLoadError';

const PosMenuModifierGroups = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { menuMap, internalMenuId } = useSelector(
    (state) => state.posMenu
  );
  const {
    items: providerItems,
    loading: providersLoading,
    providerId,
  } = useSelector((state) => state.providers);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingModifierGroup, setDeletingModifierGroup] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicatingMgId, setDuplicatingMgId] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSourceMg, setDuplicateSourceMg] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateTitle, setDuplicateTitle] = useState('');
  const [duplicateOptions, setDuplicateOptions] = useState(false);
  const [duplicateOptionPrices, setDuplicateOptionPrices] = useState({});

  // Get business ID from localStorage
  const businessId = JSON.parse(localStorage.getItem('businessInfo'))?.id;

  // Check if providers have been loaded
  const providersLoaded = providerId !== null || providerItems?.length > 0;

  // Check if POS is connected
  const isPosConnected = isPosProviderConnected(providerItems);

  const { loadStatus: menuLoadStatus, retry: retryMenuLoad } =
    usePosMenuMap(businessId);

  // Convert modifier groups object to array
  const allModifierGroups = menuMap?.modifierGroups
    ? Object.values(menuMap.modifierGroups)
    : [];

  // Filter modifier groups based on search term (name or internal name/title)
  const filteredModifierGroups = allModifierGroups.filter((mg) => {
    const name = getTranslationValue(mg.name).toLowerCase();
    const title = getTranslationValue(mg.title).toLowerCase();
    const query = searchTerm.toLowerCase();
    return name.includes(query) || title.includes(query);
  });

  // Sort modifier groups by name
  const sortedModifierGroups = [...filteredModifierGroups].sort((a, b) => {
    const nameA = getTranslationValue(a.name).toLowerCase();
    const nameB = getTranslationValue(b.name).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Paginate
  const paginatedModifierGroups = sortedModifierGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Deleting the only row on the last page would leave an empty page
  const lastPage = Math.max(
    0,
    Math.ceil(sortedModifierGroups.length / rowsPerPage) - 1
  );
  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditModifierGroup = (modifierGroupId) => {
    history.push(`/pos-menu/modifier-groups/${modifierGroupId}`);
  };

  const handleAddModifierGroup = () => {
    history.push('/pos-menu/modifier-groups/new');
  };

  const handleOpenDeleteDialog = (modifierGroup) => {
    setDeletingModifierGroup(modifierGroup);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingModifierGroup(null);
  };

  const handleDelete = async () => {
    if (!deletingModifierGroup) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteModifierGroup(deletingModifierGroup._id));
      toast.success('Modifier group deleted successfully');
      handleCloseDeleteDialog();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const getOptionsCount = (modifierGroup) => {
    // Use optionIds as the source of truth for options count
    // optionIds is maintained by the reducer and is always up-to-date
    if (Array.isArray(modifierGroup.optionIds)) {
      return modifierGroup.optionIds.length;
    }
    if (Array.isArray(modifierGroup.options)) {
      return modifierGroup.options.length;
    }
    return 0;
  };

  const handleOpenDuplicateDialog = (mg) => {
    if (!internalMenuId) {
      toast.error('Menu information is not loaded. Please refresh the page.');
      return;
    }
    const originalName = getTranslationValue(mg.name);
    const originalTitle = getTranslationValue(mg.title);
    setDuplicateSourceMg(mg);
    setDuplicateName(originalName);
    setDuplicateTitle(originalTitle || '');
    setDuplicateOptions(false);
    // Initialize option prices from the source modifier group's options
    const optionPrices = {};
    (mg.optionIds || []).forEach((optionId) => {
      const item = menuMap?.items?.[optionId];
      if (item) {
        optionPrices[optionId] = item.price || 0;
      }
    });
    setDuplicateOptionPrices(optionPrices);
    setDuplicateDialogOpen(true);
  };

  const handleCloseDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicateSourceMg(null);
    setDuplicateName('');
    setDuplicateTitle('');
    setDuplicateOptions(false);
    setDuplicateOptionPrices({});
  };

  // Helper to ensure translation objects have proper structure
  const ensureTranslationObj = (obj) => {
    if (!obj || typeof obj !== 'object' || !('en' in obj)) {
      return { en: '' };
    }
    return obj;
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateSourceMg || !internalMenuId) return;
    if (!duplicateName.trim()) {
      toast.error('Name is required');
      return;
    }

    const mg = duplicateSourceMg;
    const shouldDuplicateOptions = duplicateOptions;
    // Save custom prices before closing dialog (which resets state)
    const customPrices = { ...duplicateOptionPrices };
    setDuplicatingMgId(mg._id);
    handleCloseDuplicateDialog();

    try {
      // Build name object with user-provided name
      const nameObj = { en: duplicateName.trim() };

      // Build title object with user-provided title
      const titleObj = duplicateTitle.trim()
        ? { en: duplicateTitle.trim() }
        : { en: '' };

      const duplicateData = {
        name: nameObj,
        title: titleObj,
        description: mg.description || { en: '' },
        min: mg.min ?? null,
        max: mg.max ?? null,
        free: mg.free ?? null,
        isOnePrice: mg.isOnePrice || false,
        tags: mg.tags || [],
        sort: 0,
        canSelectMultipleOptions: mg.canSelectMultipleOptions || false,
        minAnySingleModifierItems: mg.minAnySingleModifierItems ?? null,
        maxAnySingleModifierItems: mg.maxAnySingleModifierItems ?? null,
        minTotalNumberOfModifierItems: mg.minTotalNumberOfModifierItems ?? null,
        maxTotalNumberOfModifierItems: mg.maxTotalNumberOfModifierItems ?? null,
        applyConditionWithMultiplier:
          mg.applyConditionWithMultiplier ?? null,
        conditionIsAppliedForProduct:
          mg.conditionIsAppliedForProduct ?? null,
        applyValidationToItemsActualQuantity:
          mg.applyValidationToItemsActualQuantity ?? null,
        sortParentOptionsByThisOptionsSorting:
          mg.sortParentOptionsByThisOptionsSorting ?? null,
        groupAllParentsByThisOption:
          mg.groupAllParentsByThisOption ?? null,
        priceType: mg.priceType ?? null,
        hideNameOnOrder: mg.hideNameOnOrder ?? false,
        showNestedLayout: mg.showNestedLayout ?? null,
        inOrderViewLayout: mg.inOrderViewLayout ?? null,
        menu: internalMenuId,
      };

      const createdMg = await dispatch(createModifierGroup(duplicateData));

      // Copy options from the original modifier group
      const originalOptions = mg.optionIds || [];
      let optionsCopied = 0;
      let failedOptions = 0;
      let failedNestedGroups = 0;

      if (originalOptions.length > 0) {
        for (let i = 0; i < originalOptions.length; i++) {
          try {
            let optionIdToAssign = originalOptions[i];
            let nestedGroupIdsToCopy = [];

            // If duplicating options, create a copy of each item first
            if (shouldDuplicateOptions) {
              const originalItem = menuMap?.items?.[originalOptions[i]];
              if (originalItem) {
                // Build image object
                let imageObj = { url: '', fileId: '' };
                if (originalItem.image) {
                  imageObj = {
                    url: originalItem.image.url || '',
                    fileId:
                      originalItem.image.fileId || originalItem.image.key || '',
                  };
                }

                // Use custom price if set, otherwise use original price
                const optionPrice =
                  customPrices[originalOptions[i]] !== undefined
                    ? typeof customPrices[originalOptions[i]] === 'number'
                      ? customPrices[originalOptions[i]]
                      : parseFloat(customPrices[originalOptions[i]]) || 0
                    : originalItem.price || 0;

                // Title = option name + modifier group title
                const optionName = getTranslationValue(originalItem.name) || '';
                const mgTitle = titleObj.en || nameObj.en || '';
                const newItemTitle = `${optionName} ${mgTitle}`.trim();

                const duplicateItemData = {
                  name: originalItem.name || { en: '' },
                  title: { en: newItemTitle },
                  description: ensureTranslationObj(originalItem.description),
                  price: optionPrice,
                  tax: originalItem.tax ?? null,
                  sort: 0,
                  tags: originalItem.tags || [],
                  isOnePrice: originalItem.isOnePrice || false,
                  image: imageObj,
                  soldOut: { status: 'available', soldOutDate: null },
                  onItsOwn: originalItem.onItsOwn || false,
                  isSizeOption: originalItem.isSizeOption || false,
                  kitchenInstruction: ensureTranslationObj(
                    originalItem.kitchenInstruction
                  ),
                  recipe: ensureTranslationObj(originalItem.recipe),
                  minAllowedQuantity: originalItem.minAllowedQuantity ?? null,
                  maxAllowedQuantity: originalItem.maxAllowedQuantity ?? null,
                  minTotalNumberOfModifierGroups:
                    originalItem.minTotalNumberOfModifierGroups ?? null,
                  maxTotalNumberOfModifierGroups:
                    originalItem.maxTotalNumberOfModifierGroups ?? null,
                  minTotalNumberOfModifierItems:
                    originalItem.minTotalNumberOfModifierItems ?? null,
                  maxTotalNumberOfModifierItems:
                    originalItem.maxTotalNumberOfModifierItems ?? null,
                  isPopular: originalItem.isPopular || false,
                  priceType: originalItem.priceType ?? null,
                  hideNameOnOrder: originalItem.hideNameOnOrder ?? false,
                  showNestedLayout: originalItem.showNestedLayout ?? null,
                  inOrderViewLayout: originalItem.inOrderViewLayout ?? null,
                  availableInKiosk: originalItem.availableInKiosk ?? false,
                  scheduleIds: originalItem.scheduleIds || [],
                  printerTags: originalItem.printerTags || [],
                  menu: internalMenuId,
                };

                const createdItem = await dispatch(
                  createItem(duplicateItemData)
                );
                optionIdToAssign = createdItem._id;
                nestedGroupIdsToCopy = originalItem.modifierGroupIds || [];
              }
            }

            await dispatch(
              createModifierGroupOption({
                modifierGroupId: createdMg._id,
                optionId: optionIdToAssign,
                menuId: internalMenuId,
                sort: i,
              })
            );
            optionsCopied++;

            // The duplicated option keeps the source option's nested groups
            for (let j = 0; j < nestedGroupIdsToCopy.length; j++) {
              try {
                await dispatch(
                  createItemModifierGroup({
                    itemId: optionIdToAssign,
                    modifierGroupId: nestedGroupIdsToCopy[j],
                    menuId: internalMenuId,
                    sort: j,
                  })
                );
              } catch (nestedError) {
                failedNestedGroups++;
              }
            }
          } catch (optionError) {
            // Continue with other options even if one fails
            failedOptions++;
          }
        }
      }

      const problems = [
        failedOptions > 0 &&
          `${failedOptions} of ${originalOptions.length} option(s) couldn't be copied`,
        failedNestedGroups > 0 &&
          `${failedNestedGroups} nested modifier group link(s) couldn't be copied`,
      ].filter(Boolean);

      if (problems.length > 0) {
        toast.warning(`"${nameObj.en}" created, but ${problems.join('; ')}`);
      } else {
        const optionsMessage =
          optionsCopied > 0
            ? ` with ${optionsCopied} ${
                shouldDuplicateOptions ? 'duplicated' : ''
              } option(s)`
            : '';
        toast.success(`"${nameObj.en}" created successfully${optionsMessage}`);
      }
    } catch (error) {
      console.error('Failed to duplicate modifier group:', error);
      toast.error(error.message || 'Failed to duplicate modifier group');
    } finally {
      setDuplicatingMgId(null);
    }
  };

  const isPageLoading = duplicatingMgId !== null || isDeleting;

  // Show loading while providers are loading or haven't been fetched yet
  if (providersLoading || !providersLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Only show "POS Not Connected" after providers have finished loading
  if (!isPosConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            POS Not Connected
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Please connect your business to a POS system to access the POS Menu
            Modifier Groups.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Go to <strong>Providers</strong> to connect your POS system.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Modifier Groups Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddModifierGroup}
        >
          Add Modifier Group
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TextField
            label="Search Modifier Groups"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          {menuLoadStatus === 'error' ? (
            <MenuLoadError onRetry={retryMenuLoad} />
          ) : menuLoadStatus !== 'ready' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>
                        Min
                      </TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>
                        Max
                      </TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>
                        Free
                      </TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>
                        Options
                      </TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>
                        Multi-Select
                      </TableCell>
                      <TableCell align="right" sx={{ width: 130 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedModifierGroups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {searchTerm
                            ? `No modifier groups found matching "${searchTerm}"`
                            : 'No modifier groups found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedModifierGroups.map((mg) => (
                        <TableRow
                          key={mg._id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleEditModifierGroup(mg._id)}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {getTranslationValue(mg.name)}
                            </Typography>
                            {mg.title?.en && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {getTranslationValue(mg.title)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{mg.min ?? '-'}</TableCell>
                          <TableCell align="center">{mg.max ?? '-'}</TableCell>
                          <TableCell align="center">{mg.free ?? '-'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getOptionsCount(mg)}
                              size="small"
                              color={
                                getOptionsCount(mg) > 0 ? 'primary' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            {mg.canSelectMultipleOptions ? (
                              <Chip
                                label="Yes"
                                size="small"
                                sx={{
                                  bgcolor: '#e3f2fd',
                                  color: '#1976d2',
                                }}
                              />
                            ) : (
                              <Chip
                                label="No"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditModifierGroup(mg._id);
                                }}
                                size="small"
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="default"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDuplicateDialog(mg);
                                }}
                                size="small"
                                title="Duplicate"
                                disabled={duplicatingMgId === mg._id}
                              >
                                {duplicatingMgId === mg._id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <ContentCopyIcon />
                                )}
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteDialog(mg);
                                }}
                                size="small"
                                title="Delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={sortedModifierGroups.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'nowrap',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      margin: 0,
                    },
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete &quot;
            {getTranslationValue(deletingModifierGroup?.name) ||
              'this modifier group'}
            &quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            color="primary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            autoFocus
            disabled={isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Modifier Group Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={handleCloseDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Modifier Group</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the name and internal name for the duplicated modifier group.
          </DialogContentText>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            required
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Internal Name (optional)"
            fullWidth
            value={duplicateTitle}
            onChange={(e) => setDuplicateTitle(e.target.value)}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            helperText="Used for internal reference only"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={duplicateOptions}
                onChange={(e) => setDuplicateOptions(e.target.checked)}
              />
            }
            label="Duplicate options (create copies of items instead of linking existing ones)"
          />

          {/* Show options list with editable prices when duplicateOptions is checked */}
          {duplicateOptions && duplicateSourceMg?.optionIds?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Options to duplicate ({duplicateSourceMg.optionIds.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {duplicateSourceMg.optionIds.map((optionId) => {
                  const item = menuMap?.items?.[optionId];
                  if (!item) return null;
                  const itemName = getTranslationValue(item.name) || 'Unnamed';
                  const itemTitle = getTranslationValue(item.title);
                  return (
                    <Box
                      key={optionId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': {
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {itemName}
                          {itemTitle && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ color: 'text.secondary', ml: 1 }}
                            >
                              ({itemTitle})
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="text"
                        value={
                          duplicateOptionPrices[optionId] != null
                            ? formatNumber(duplicateOptionPrices[optionId])
                            : ''
                        }
                        onChange={(e) => {
                          const transformed = priceTransform(e.target.value);
                          setDuplicateOptionPrices((prev) => ({
                            ...prev,
                            [optionId]: transformed,
                          }));
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDuplicateDialog}
            disabled={duplicatingMgId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDuplicate}
            variant="contained"
            disabled={!duplicateName.trim() || duplicatingMgId !== null}
            startIcon={
              duplicatingMgId ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {duplicatingMgId ? 'Creating...' : 'Create Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isPageLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          color: '#1976d2',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2' }}>
          Processing...
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default PosMenuModifierGroups;
